/**
 * XIV Dye Tools v2.1.0 - Modal Service
 *
 * Modal dialog system for user interactions
 * Follows ToastService singleton pattern with subscription model
 *
 * @module services/modal-service
 */

import { logger } from '@shared/logger';

// ============================================================================
// Modal Types
// ============================================================================

/**
 * Modal type determines content and behavior
 */
export type ModalType = 'welcome' | 'changelog' | 'confirm' | 'custom';

/**
 * Branded type for Modal IDs (WEB-TYPE-001)
 * Prevents accidental use of arbitrary strings as modal identifiers
 */
export type ModalId = string & { readonly __brand: 'ModalId' };

/**
 * Modal configuration for creating a modal
 */
export interface ModalConfig {
  type: ModalType;
  title: string;
  content?: HTMLElement;
  size?: 'sm' | 'md' | 'lg';
  closable?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  /**
   * Called when the footer cancel/secondary button is pressed (before dismiss).
   * Lets the secondary action do real work (e.g. Welcome's "Take the tour")
   * instead of only dismissing.
   */
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  // -------------------------------------------------------------------------
  // 5.0 (16A) shell options
  // -------------------------------------------------------------------------
  /**
   * Shell variant (16A): `sheet` = the default dialog (bottom sheet on
   * mobile with grab handle + drag-to-close, centered 560px on desktop);
   * `panel` = 480px right slide-over for settings-like surfaces; `card` =
   * centered card; `alert` = compact centered alert (destructive confirms).
   */
  variant?: 'sheet' | 'panel' | 'card' | 'alert';
  /** Mono eyebrow line above the title */
  eyebrow?: string;
  /** Subtitle under the title */
  subtitle?: string;
  /**
   * Sheet height class (mobile): `content` hugs the content, `tall` ≈ 60%,
   * `full` = the 88% camera/full-bleed height. The old 60vh cap is dead.
   */
  sheetHeight?: 'content' | 'tall' | 'full';
  /**
   * Destructive convention (suite-wide): primary = solid accent,
   * destructive = OUTLINED accent, Cancel is the wide thumb target.
   */
  destructive?: boolean;
  /**
   * Reduced backdrop scrim — for modals that live-preview changes behind
   * the dialog (the theme picker repaints the whole app on tap; dimming
   * it was hiding the feature).
   */
  lightScrim?: boolean;
  /**
   * Panel variant only: desktop slide-over width in px (default 480).
   * The Advanced Options surface is drawn at 392.
   */
  panelWidth?: number;
}

/**
 * Modal instance with ID and timestamp
 */
export interface Modal extends ModalConfig {
  id: ModalId;
  timestamp: number;
}

// ============================================================================
// Modal Constants
// ============================================================================

/**
 * Maximum number of stacked modals
 */
const MAX_MODALS = 3;

// ============================================================================
// Modal Service Class
// ============================================================================

/**
 * Service for managing modal dialogs
 * Static singleton pattern with subscription model
 */
export class ModalService {
  private static modals: Modal[] = [];
  private static listeners: Set<(modals: Modal[]) => void> = new Set();
  /** Modal surfaces that draw themselves (`registerExternal`) — open, but not the container's to draw. */
  private static external: Set<symbol> = new Set();

  /**
   * Generate unique modal ID (WEB-TYPE-001: returns branded ModalId type)
   */
  private static generateId(): ModalId {
    return `modal_${Date.now()}_${Math.random().toString(36).substring(2, 9)}` as ModalId;
  }

  /**
   * Notify all listeners of modal changes
   */
  private static notifyListeners(): void {
    const modalsCopy = [...this.modals];
    this.listeners.forEach((listener) => listener(modalsCopy));
  }

  /**
   * Show a modal dialog
   * @returns Modal ID for programmatic dismissal
   */
  static show(config: ModalConfig): ModalId {
    const id = this.generateId();

    const modal: Modal = {
      ...config,
      id,
      size: config.size ?? 'md',
      closable: config.closable ?? true,
      closeOnBackdrop: config.closeOnBackdrop ?? true,
      closeOnEscape: config.closeOnEscape ?? true,
      timestamp: Date.now(),
    };

    // Add modal, respecting max limit
    this.modals.push(modal);
    if (this.modals.length > MAX_MODALS) {
      const removed = this.modals.shift();
      if (removed?.onClose) {
        try {
          removed.onClose();
        } catch (error) {
          logger.error('Modal onClose callback failed:', error);
        }
      }
    }

    this.notifyListeners();
    logger.info(`Modal shown: [${config.type}] ${config.title}`);

    return id;
  }

  /**
   * Show a welcome modal
   */
  static showWelcome(config: Omit<ModalConfig, 'type'>): ModalId {
    return this.show({ ...config, type: 'welcome' });
  }

  /**
   * Show a changelog modal
   */
  static showChangelog(config: Omit<ModalConfig, 'type'>): ModalId {
    return this.show({ ...config, type: 'changelog' });
  }

  /**
   * Show a confirmation modal
   */
  static showConfirm(config: Omit<ModalConfig, 'type'>): ModalId {
    return this.show({
      ...config,
      type: 'confirm',
      closable: config.closable ?? false,
      closeOnBackdrop: config.closeOnBackdrop ?? false,
    });
  }

  /**
   * Dismiss a specific modal by ID
   * WEB-PERF-001: Removed debug console.log statements for production performance
   */
  static dismiss(id: ModalId | string): void {
    const index = this.modals.findIndex((m) => m.id === id);
    if (index !== -1) {
      const modal = this.modals[index];
      this.modals.splice(index, 1);
      if (modal.onClose) {
        try {
          modal.onClose();
        } catch (error) {
          logger.error('Modal onClose callback failed:', error);
        }
      }
      this.notifyListeners();
      logger.info(`Modal dismissed: ${id}`);
    } else {
      logger.warn(`Modal not found for dismissal: ${id}`);
    }
  }

  /**
   * Dismiss the topmost modal
   * WEB-PERF-001: Removed debug console.log statements for production performance
   */
  static dismissTop(): void {
    if (this.modals.length > 0) {
      const topModal = this.modals[this.modals.length - 1];
      this.dismiss(topModal.id);
    }
  }

  /**
   * Dismiss all modals
   *
   * @testonly bulk-clear used by the container tests.
   */
  static dismissAll(): void {
    // Call onClose for each modal (wrapped in try-catch to ensure all are processed)
    this.modals.forEach((modal) => {
      if (modal.onClose) {
        try {
          modal.onClose();
        } catch (error) {
          logger.error('Modal onClose callback failed:', error);
        }
      }
    });

    this.modals = [];
    this.notifyListeners();
    logger.info('All modals dismissed');
  }

  /**
   * Get current modals (readonly copy)
   *
   * @testonly how ~27 references in modal-service.test.ts observe the
   * service; components render from `subscribe()`.
   */
  static getModals(): readonly Modal[] {
    return [...this.modals];
  }

  /**
   * Get topmost modal
   */
  static getTopModal(): Modal | null {
    return this.modals.length > 0 ? this.modals[this.modals.length - 1] : null;
  }

  /**
   * Check if any modals are open — the container's, or one that draws itself
   */
  static hasOpenModals(): boolean {
    return this.modals.length > 0 || this.external.size > 0;
  }

  /**
   * Register a modal that renders itself instead of through the container
   * (the Glamour Reader's export sheet is a full-height surface of its own).
   * While it is registered `hasOpenModals()` is true, so the global keyboard
   * shortcuts and first-visit prompts stand down exactly as they do for a
   * container modal; subscribers are not notified, since there is nothing for
   * the container to draw. Returns the release, which is safe to call twice.
   *
   * That is ALL registration does — no backdrop, focus trap, stacking limit,
   * Escape handling or close-on-navigate; the surface keeps its own. So it is
   * also how a popover that holds focus tells the page "an overlay is open"
   * (BUG-091: the twin picker and the item-links menu). Besides the
   * first-visit tutorial prompt, the readers are all keyboard ones:
   * KeyboardService's shortcuts, DyeSelector's "/", and the toast container,
   * which leaves Escape to the overlay (BUG-105). Release on every
   * close path — a leaked registration switches the shortcuts off until a
   * reload.
   */
  static registerExternal(): () => void {
    const token = Symbol('external-modal');
    this.external.add(token);
    return () => {
      this.external.delete(token);
    };
  }

  /**
   * Subscribe to modal changes
   * WEB-BUG-002: Added options parameter to control immediate notification
   * @param listener - Callback function for modal changes
   * @param options - Subscription options (immediate: whether to call listener immediately)
   * @returns Unsubscribe function
   */
  static subscribe(
    listener: (modals: Modal[]) => void,
    options: { immediate?: boolean } = { immediate: true }
  ): () => void {
    this.listeners.add(listener);

    // Immediately notify with current state (opt-in, default true for backwards compatibility)
    if (options.immediate) {
      listener([...this.modals]);
    }

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Check if reduced motion is preferred
   */
  static prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
